import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, Trash2, ShoppingCart, ShieldCheck, MessageCircle, CheckCircle2, Loader2, ArrowRight, ArrowLeft, Banknote, Smartphone, AlertTriangle } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useCart } from "../../storefront/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreCart() {
  const { cart, setQty, remove, clear, total, count } = useCart();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [pinStatus, setPinStatus] = useState(null); // null | "checking" | "ok" | "fail"
  const [note, setNote] = useState("");
  const [payMethod, setPayMethod] = useState("cod");
  const [shopCfg, setShopCfg] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/shop-config`).then(({ data }) => setShopCfg(data)).catch(() => {});
  }, []);

  const checkPincode = async (pin) => {
    setPincode(pin);
    const digits = pin.replace(/\D/g, "");
    if (digits.length !== 6) {
      setPinStatus(null);
      return;
    }
    setPinStatus("checking");
    try {
      const { data } = await axios.get(`${API}/public/delivery-check`, { params: { pincode: digits } });
      setPinStatus(data.serviceable ? "ok" : "fail");
    } catch {
      setPinStatus(null);
    }
  };

  const goPayment = (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length !== 10) return;
    if (pincode.replace(/\D/g, "").length === 6 && pinStatus === "fail") return;
    setStep(2);
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/online-orders`, {
        customer_name: name,
        phone,
        email,
        address,
        note,
        delivery_pincode: pincode.replace(/\D/g, ""),
        payment_method: payMethod,
        source: "website",
        items: cart.map((c) => ({ medicine_name: c.name, quantity: c.qty })),
      });
      setOrder(data);
      clear();
    } catch {
      setOrder({ error: true });
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    if (order.error) {
      return (
        <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="order-error-card">
          <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Order place nahi ho paya</h1>
          <p className="mt-3 text-sm text-slate-600">Kuch gadbad ho gayi. Dobara try karein ya WhatsApp par order karein.</p>
          <button onClick={() => setOrder(null)} data-testid="order-retry-btn" className="mt-6 inline-flex items-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] text-white font-semibold px-5 py-3 rounded-full text-sm">
            Dobara try karein
          </button>
        </div>
      );
    }
    const waText = encodeURIComponent(`Namaste Trustmeds Pharma! Mera order ${order.order_no || ""} — ${name}, ${phone}`);
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="order-success-confirmation">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Order mil gaya!</h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Order No: <span className="font-mono-num font-bold text-emerald-800" data-testid="order-success-no">{order.order_no}</span><br />
          Payment: <span className="font-semibold" data-testid="order-success-payment">{order.payment_method === "upi" ? "UPI (verify ho raha hai)" : "Cash on Delivery"}</span><br />
          Hamari team aapko <span className="font-semibold">{phone}</span> par confirm karne ke liye call karegi.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to={`/store/track?phone=${encodeURIComponent(phone.replace(/\D/g, "").slice(-10))}`} data-testid="order-track-link" className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-5 py-3 rounded-full text-sm">
            Track Your Order <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={`https://wa.me/917428811670?text=${waText}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold px-5 py-3 rounded-full text-sm">
            <MessageCircle className="w-4 h-4" /> WhatsApp par confirm karein
          </a>
          <Link to="/store/shop" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold px-5 py-3 rounded-full text-sm hover:bg-slate-50">
            Aur shopping karein <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="store-cart-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-6">Your Cart</h1>
      {cart.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-3xl">
          <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Cart khaali hai.</p>
          <Link to="/store/shop" className="mt-4 inline-flex items-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] text-white font-semibold px-6 py-3 rounded-full text-sm">
            Shop karein <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-3 space-y-3">
            {cart.map((c) => (
              <div key={c.id} data-testid={`cart-item-${c.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 font-mono-num">₹{c.price.toFixed(2)} / {(c.unit_label || "Strip").toLowerCase()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setQty(c.id, c.qty - 1)} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-50" data-testid={`cart-minus-${c.id}`}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-6 text-center font-mono-num font-semibold text-sm">{c.qty}</span>
                  <button onClick={() => setQty(c.id, c.qty + 1)} className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-50" data-testid={`cart-plus-${c.id}`}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <div className="w-20 text-right font-mono-num font-bold text-slate-900 text-sm">₹{(c.price * c.qty).toFixed(2)}</div>
                <button onClick={() => remove(c.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-full" data-testid={`cart-remove-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-5 lg:sticky lg:top-20">
            <div className="flex items-center gap-2 mb-4" data-testid="checkout-steps">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === 1 ? "bg-[#064E3B] text-white" : "bg-emerald-100 text-emerald-800"}`}>1</span>
              <span className={`text-xs font-semibold ${step === 1 ? "text-slate-900" : "text-slate-400"}`}>Details</span>
              <div className="flex-1 h-px bg-slate-200" />
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? "bg-[#064E3B] text-white" : "bg-slate-100 text-slate-400"}`}>2</span>
              <span className={`text-xs font-semibold ${step === 2 ? "text-slate-900" : "text-slate-400"}`}>Payment</span>
            </div>

            <div className="flex justify-between text-sm"><span className="text-slate-500">Items</span><span className="font-mono-num font-semibold">{count}</span></div>
            <div className="flex justify-between text-base font-bold border-t border-slate-100 pt-3 mt-2 mb-4"><span>Total (approx)</span><span className="font-mono-num text-emerald-800" data-testid="cart-total">₹{total.toFixed(2)}</span></div>

            {step === 1 ? (
              <form onSubmit={goPayment} className="space-y-3" data-testid="checkout-details-form">
                <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name *" data-testid="checkout-name-input"
                  className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (10 digit) *" data-testid="checkout-phone-input" inputMode="numeric"
                  className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email ID (optional)" data-testid="checkout-email-input"
                  className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <textarea required value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery Address (Kashipur / nearby) *" data-testid="checkout-address-input" rows={2}
                  className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <div>
                  <input required value={pincode} onChange={(e) => checkPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Pincode (6 digit) *" data-testid="checkout-pincode-input" inputMode="numeric"
                    className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                  {pinStatus === "checking" && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1" data-testid="pincode-checking"><Loader2 className="w-3 h-3 animate-spin" /> Delivery check ho rahi hai…</p>}
                  {pinStatus === "ok" && <p className="text-xs text-emerald-700 font-semibold mt-1 flex items-center gap-1" data-testid="pincode-serviceable"><CheckCircle2 className="w-3.5 h-3.5" /> Is pincode par delivery available hai</p>}
                  {pinStatus === "fail" && <p className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1" data-testid="pincode-unserviceable"><AlertTriangle className="w-3.5 h-3.5" /> Is pincode par delivery nahi hai — WhatsApp par order karein</p>}
                </div>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note / prescription detail (optional)" data-testid="checkout-note-input"
                  className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <button type="submit" data-testid="checkout-continue-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] text-white font-semibold py-3.5 rounded-full transition-colors">
                  Continue to Payment <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <div className="space-y-3" data-testid="checkout-payment-step">
                <button onClick={() => setStep(1)} data-testid="checkout-back-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline">
                  <ArrowLeft className="w-3.5 h-3.5" /> Details badalni hain?
                </button>

                <div className="grid grid-cols-2 gap-2" data-testid="payment-methods">
                  <button type="button" onClick={() => setPayMethod("cod")} data-testid="pay-method-cod"
                    className={`p-3 rounded-2xl border-2 text-left transition-colors ${payMethod === "cod" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Banknote className={`w-5 h-5 mb-1.5 ${payMethod === "cod" ? "text-emerald-700" : "text-slate-400"}`} />
                    <div className="text-sm font-bold text-slate-900">Cash on Delivery</div>
                    <div className="text-[11px] text-slate-500">Order milne par cash/UPI dena</div>
                  </button>
                  <button type="button" onClick={() => setPayMethod("upi")} data-testid="pay-method-upi"
                    className={`p-3 rounded-2xl border-2 text-left transition-colors ${payMethod === "upi" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Smartphone className={`w-5 h-5 mb-1.5 ${payMethod === "upi" ? "text-emerald-700" : "text-slate-400"}`} />
                    <div className="text-sm font-bold text-slate-900">UPI QR</div>
                    <div className="text-[11px] text-slate-500">Abhi scan karke pay karein</div>
                  </button>
                </div>

                {payMethod === "upi" && (
                  shopCfg?.upi_id ? (
                    <div className="flex flex-col items-center gap-2 p-4 border border-slate-200 rounded-2xl bg-slate-50" data-testid="checkout-upi-qr">
                      <QRCodeCanvas
                        value={`upi://pay?pa=${encodeURIComponent(shopCfg.upi_id)}&pn=${encodeURIComponent(shopCfg.upi_payee_name || "Trustmeds Pharma")}&am=${total.toFixed(2)}&cu=INR&tn=Trustmeds Order`}
                        size={170}
                        data-testid="checkout-upi-qr-code"
                      />
                      <div className="text-xs font-mono-num font-semibold text-slate-700">{shopCfg.upi_id}</div>
                      <div className="text-[11px] text-slate-500 text-center leading-relaxed">PhonePe / GPay / Paytm se scan karke <span className="font-bold text-slate-700">₹{total.toFixed(2)}</span> pay karein, phir neeche Place Order dabayein</div>
                    </div>
                  ) : (
                    <div className="p-3 border border-amber-200 bg-amber-50 rounded-xl text-xs text-amber-700" data-testid="upi-unavailable">UPI abhi available nahi hai — Cash on Delivery choose karein.</div>
                  )
                )}

                <button onClick={placeOrder} disabled={placing} data-testid="checkout-submit-order-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors">
                  {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Place Order — {payMethod === "upi" ? "UPI Paid" : "Cash on Delivery"}
                </button>
                <p className="text-[11px] text-slate-400 text-center">Order ke baad hamari team call karke confirm karegi.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
